import { AuthContext } from '@urbanx/agx.auth.client';
import {
  type ContractDocuments,
  DocumentTypes,
  type StaffUpdateContractTerminationRequest,
  StaffUpdateContractTerminationRequestContractTerminationReasons,
  StaffUpdateContractTerminationRequestDepositEntitlement,
  useInvalidateUseStaffGetContract,
  useStaffUpdateContractTermination,
  useStaffGetContractDeposits,
} from '@urbanx/agx.campaigns.client';
import type { UploadedDocument } from '@urbanx/agx.campaigns.client';
import { type RadioGroupOptions, useToast, YesNo } from '@urbanx/agx.ui-components2';
import type { AxiosProgressEvent, GenericAbortSignal } from 'axios';
import { useSelectedCampaign } from 'hooks';
import { waitForTaskCollectionRefresh } from 'hooks/taskCollectionRefresh';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { uploadFile } from 'services';

const DEPOSIT_OPTIONS: RadioGroupOptions[] = [
  {
    label: 'Buyer',
    value: StaffUpdateContractTerminationRequestDepositEntitlement.Buyer,
  },
  {
    label: 'Vendor',
    value: StaffUpdateContractTerminationRequestDepositEntitlement.Vendor,
  },
  {
    label: 'No deposit held',
    value: StaffUpdateContractTerminationRequestDepositEntitlement.NoDepositReceived,
  },
];

const REASON_OPTIONS: RadioGroupOptions[] = [
  {
    label: 'Cooling off period',
    value: StaffUpdateContractTerminationRequestContractTerminationReasons.CoolingOffPeriod,
  },
  {
    label: 'Failed Finance',
    value: StaffUpdateContractTerminationRequestContractTerminationReasons.FailedFinance,
  },
  {
    label: 'Failed Building & Pest',
    value: StaffUpdateContractTerminationRequestContractTerminationReasons.FailedBuildingAndPest,
  },
  {
    label: 'Special Conditions',
    value: StaffUpdateContractTerminationRequestContractTerminationReasons.SpecialConditions,
  },
];

interface UseContractTerminateProps {
  isOpen: boolean;
  setIsOpen: (_value: boolean) => void;
  onContractTerminated?: () => void;
}

export const useContractTerminate = (props: UseContractTerminateProps) => {
  const { onContractTerminated, isOpen, setIsOpen } = props;
  const { campaign: selectedCampaign } = useSelectedCampaign();
  const { contractId } = useParams();
  const { data: contractDeposits, isError: allocationError } = useStaffGetContractDeposits(
    contractId ?? '',
    isOpen && !!contractId
  );
  const allocation = contractDeposits?.terminationAllocation;
  const [depositHeldInTrust, setDepositHeldInTrust] = useState<boolean>();
  const [vendorChargesPenalty, setVendorChargesPenalty] = useState<boolean>();
  const [submitting, setIsSubmitting] = useState(false);
  const { getAuthToken } = useContext(AuthContext);
  const { pushToast } = useToast();
  const [selectedReason, setSelectedReason] = useState<
    StaffUpdateContractTerminationRequestContractTerminationReasons | undefined
  >(undefined);
  const [selectedDepositOption, setSelectedDepositOption] = useState<
    StaffUpdateContractTerminationRequestDepositEntitlement | undefined
  >(undefined);
  const [selectedTerminationDate, setSelectedTerminationDate] = useState<Date | null>(new Date());
  const [vendorTerminationConfirmationFiles, setVendorTerminationConfirmationFiles] = useState<
    (UploadedDocument | File)[]
  >([]);
  const [buyerTerminationConfirmationFiles, setBuyerTerminationConfirmationFiles] = useState<
    (UploadedDocument | File)[]
  >([]);
  const invalidateUseStaffGetContract = useInvalidateUseStaffGetContract();
  const { staffUpdateContractTermination } = useStaffUpdateContractTermination({
    onSuccess: () => {
      if (contractId) {
        invalidateUseStaffGetContract(contractId);
      }
    },
    onError: () => {},
  });

  const buildRenamedFormData = (formData: FormData, documentType: DocumentTypes) => {
    const file = formData.get('File');
    if (!file || typeof File === 'undefined' || !(file instanceof File)) {
      return formData;
    }

    const renamedFile = new File([file], `${contractId}/${documentType}/${file.name}`, {
      type: file.type,
      lastModified: file.lastModified,
    });
    const renamedFormData = new FormData();
    renamedFormData.append('File', renamedFile);
    return renamedFormData;
  };

  const onUploadFile = async (
    authToken: string,
    file: string | FormData,
    uploadCampaignId: string,
    uploadedFormType: DocumentTypes,
    uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    abortSignal?: GenericAbortSignal,
    uploadContractId?: string
  ) => {
    const renamedFormData =
      typeof FormData !== 'undefined' && file instanceof FormData
        ? buildRenamedFormData(file, uploadedFormType)
        : file;

    return await uploadFile(
      authToken,
      renamedFormData,
      uploadCampaignId,
      uploadedFormType,
      uploadProgress ?? null,
      abortSignal ?? null,
      undefined,
      uploadContractId
    );
  };

  const uploadSelectedFile = async (
    authToken: string,
    uploadCampaignId: string,
    documentType: DocumentTypes,
    selectedFile?: UploadedDocument | File,
    uploadContractId?: string
  ) => {
    if (!selectedFile || !(selectedFile instanceof File)) return selectedFile;

    const formData = new FormData();
    formData.append('File', selectedFile);

    return onUploadFile(
      authToken,
      formData,
      uploadCampaignId,
      documentType,
      undefined,
      undefined,
      uploadContractId
    );
  };

  const isCoolingOff =
    selectedReason ===
    StaffUpdateContractTerminationRequestContractTerminationReasons.CoolingOffPeriod;
  const outcomeComplete = isCoolingOff
    ? depositHeldInTrust === false ||
      (depositHeldInTrust === true &&
        vendorChargesPenalty !== undefined &&
        (vendorChargesPenalty === false ||
          (!allocationError &&
            allocation?.terminationPenalty != null &&
            allocation.terminationPenalty > 0)))
    : selectedDepositOption !== undefined;

  const onTerminateConfirm = async () => {
    if (
      !selectedCampaign?.campaignId ||
      !contractId ||
      !selectedTerminationDate ||
      !outcomeComplete ||
      !selectedReason ||
      vendorTerminationConfirmationFiles.length === 0 ||
      buyerTerminationConfirmationFiles.length === 0
    ) {
      return;
    }

    setIsSubmitting(true);
    let cancelTaskCollectionRefresh: (() => void) | undefined;

    try {
      const authToken = await getAuthToken();
      if (!authToken) return;

      let vendorTerminationConfirmation: UploadedDocument | undefined;
      let buyerTerminationConfirmation: UploadedDocument | undefined;
      try {
        vendorTerminationConfirmation = await uploadSelectedFile(
          authToken,
          selectedCampaign.campaignId,
          DocumentTypes.VendorTerminationConfirmation,
          vendorTerminationConfirmationFiles[0],
          contractId
        );

        buyerTerminationConfirmation = await uploadSelectedFile(
          authToken,
          selectedCampaign.campaignId,
          DocumentTypes.BuyerTerminationConfirmation,
          buyerTerminationConfirmationFiles[0],
          contractId
        );
      } catch (error) {
        pushToast({
          type: 'error',
          message:
            error instanceof Error
              ? error.message
              : 'The termination confirmation could not be uploaded. Check the file and try again.',
        });
        return;
      }

      if (!vendorTerminationConfirmation || !buyerTerminationConfirmation) {
        return;
      }

      const contractDocuments: ContractDocuments = {
        vendorTerminationConfirmation,
        buyerTerminationConfirmation,
      };

      const body: StaffUpdateContractTerminationRequest = {
        terminationDate: selectedTerminationDate,
        depositEntitlement: isCoolingOff ? undefined : selectedDepositOption,
        depositHeldInTrust: isCoolingOff ? depositHeldInTrust : undefined,
        vendorChargesPenalty: isCoolingOff && depositHeldInTrust ? vendorChargesPenalty : undefined,
        contractTerminationReasons: selectedReason,
        contractDocuments,
      };

      const taskCollectionRefresh = waitForTaskCollectionRefresh(
        selectedCampaign.campaignId,
        contractId
      );
      cancelTaskCollectionRefresh = taskCollectionRefresh.cancel;

      await staffUpdateContractTermination({
        campaignId: selectedCampaign.campaignId,
        contractId,
        body,
      });
      const taskCollectionRefreshResult = await taskCollectionRefresh.wait();

      if (taskCollectionRefreshResult === 'timed-out') {
        pushToast({
          type: 'error',
          message:
            'The contract was terminated, but the task refresh could not be confirmed. Refresh the page to check its latest status.',
        });
      }

      setIsOpen(false);
      onContractTerminated?.();
    } finally {
      cancelTaskCollectionRefresh?.();
      setIsSubmitting(false);
    }
  };

  const onTerminateSuccess = () => {
    onContractTerminated?.();
  };

  const onDateChange = (date: Date | null) => {
    setSelectedTerminationDate(date);
  };

  const onVendorTerminationConfirmationChanged = ({
    value,
  }: {
    value?: (UploadedDocument | File)[];
  }) => {
    setVendorTerminationConfirmationFiles(value ?? []);
  };

  const onBuyerTerminationConfirmationChanged = ({
    value,
  }: {
    value?: (UploadedDocument | File)[];
  }) => {
    setBuyerTerminationConfirmationFiles(value ?? []);
  };

  const onDepositOptionChange = (value: string) => {
    const recipient = Object.values(StaffUpdateContractTerminationRequestDepositEntitlement).find(
      (option) => option === value
    );
    if (recipient === undefined) throw new Error('Unknown deposit recipient');
    setSelectedDepositOption(recipient);
  };

  const onReasonChange = (value: string) => {
    const reason = Object.values(
      StaffUpdateContractTerminationRequestContractTerminationReasons
    ).find((option) => option === value);
    if (reason === undefined) throw new Error('Unknown termination reason');
    setSelectedReason(reason);
    setSelectedDepositOption(undefined);
    setDepositHeldInTrust(undefined);
    setVendorChargesPenalty(undefined);
  };

  const canSubmit =
    selectedTerminationDate != null &&
    vendorTerminationConfirmationFiles.length > 0 &&
    buyerTerminationConfirmationFiles.length > 0 &&
    selectedReason != null &&
    outcomeComplete &&
    !submitting;

  useEffect(() => {
    if (isOpen) {
      setSelectedTerminationDate((currentValue) => currentValue ?? new Date());
      return;
    }

    setSelectedDepositOption(undefined);
    setSelectedReason(undefined);
    setDepositHeldInTrust(undefined);
    setVendorChargesPenalty(undefined);
    setSelectedTerminationDate(null);
    setVendorTerminationConfirmationFiles([]);
    setBuyerTerminationConfirmationFiles([]);
    setIsSubmitting(false);
  }, [isOpen]);

  return {
    isCoolingOff,
    depositHeldInTrust,
    vendorChargesPenalty,
    allocation,
    allocationError,
    onDepositHeldChange: (value: string) => {
      setDepositHeldInTrust(value === YesNo.Yes);
      setVendorChargesPenalty(undefined);
    },
    onVendorPenaltyChange: (value: string) => setVendorChargesPenalty(value === YesNo.Yes),
    onTerminateSuccess,
    onTerminateConfirm,
    depositOptions: DEPOSIT_OPTIONS,
    reasonOptions: REASON_OPTIONS,
    onDateChange,
    canSubmit,
    selectedDepositOption,
    selectedReason,
    selectedTerminationDate,
    vendorTerminationConfirmationFiles,
    buyerTerminationConfirmationFiles,
    onVendorTerminationConfirmationChanged,
    onBuyerTerminationConfirmationChanged,
    onDepositOptionChange,
    onReasonChange,
    onUploadFile,
    getAuthToken,
    selectedCampaign,
    submitting,
  };
};
